import React, { useEffect, useRef } from 'react';
import flatpickr from 'flatpickr';
import 'flatpickr/dist/flatpickr.min.css';
import InputIconByKey from '../Input/InputIconByKey';
import { Spanish } from 'flatpickr/dist/l10n/es.js';
import RequiredAsteriskTooltip from '../Input/RequiredAsteriskTooltip';
import { IconKey } from '../../../types/system/icons';

interface DatePickerProps {
  label: string;
  name: string;
  value: string;
  onChange?: (date: string) => void;
  error?: string;
  disabled?: boolean;
  placeholder?: string;
  iconKey?: IconKey;
  required?: boolean;
  enableTime?: boolean;
  helperText?: string;
  withMargin?: boolean;
}

const DatePicker: React.FC<DatePickerProps> = ({
  label,
  name,
  value,
  onChange,
  error,
  disabled = false,
  placeholder = 'Seleccione una fecha',
  iconKey,
  required,
  enableTime,
  helperText,
  withMargin = true,
}) => {
  const datePickerRef = useRef<HTMLInputElement>(null);
  const flatpickrInstance = useRef<flatpickr.Instance | null>(null);
  const containerClass = `relative ${withMargin ? 'mb-5' : ''}`;

  useEffect(() => {
    if (datePickerRef.current && !flatpickrInstance.current) {
      flatpickrInstance.current = flatpickr(datePickerRef.current, {
        locale: Spanish,
        dateFormat: enableTime ? 'Y-m-d H:i' : 'Y-m-d',
        defaultDate: value,
        enableTime: enableTime ?? false,
        allowInput: true,
        disableMobile: true,
        static: false,
        appendTo: document.body,
        position: 'auto',
        clickOpens: !disabled,

        prevArrow:
          '<svg class="fill-current" width="7" height="11" viewBox="0 0 7 11"><path d="M5.4 10.8l1.4-1.4-4-4 4-4L5.4 0 0 5.4z" /></svg>',
        nextArrow:
          '<svg class="fill-current" width="7" height="11" viewBox="0 0 7 11"><path d="M1.4 10.8L0 9.4l4-4-4-4L1.4 0l5.4 5.4z" /></svg>',

        onReady: (_, __, instance) => {
          const rect = datePickerRef.current?.getBoundingClientRect();
          const spaceBelow = window.innerHeight - (rect?.bottom ?? 0);
          instance.set('position', spaceBelow < 300 ? 'above' : 'below');
        },

        onOpen: (_, __, instance) => {
          const calendar = instance.calendarContainer;
          const inputRect = datePickerRef.current?.getBoundingClientRect();

          if (!calendar || !inputRect) return;

          const calendarRect = calendar.getBoundingClientRect();

          const overflowRight = calendarRect.right > window.innerWidth;
          const overflowLeft = calendarRect.left < 0;

          if (overflowRight) {
            const shiftLeft = calendarRect.right - window.innerWidth + 8;
            calendar.style.left = `-${shiftLeft}px`;
          }

          if (overflowLeft) {
            calendar.style.left = '0px';
          }

          const spaceBelow = window.innerHeight - inputRect.bottom;
          const spaceAbove = inputRect.top;

          if (spaceBelow < 300 && spaceAbove > spaceBelow) {
            instance.set('position', 'above');
          } else {
            instance.set('position', 'below');
          }
        },

        onClose: (_, __, instance) => {
          const overlay = document.querySelector('.flatpickr-mobile-overlay');

          if (overlay) {
            overlay.classList.remove('visible');
            setTimeout(() => overlay.remove(), 250);
          }

          const calendar = instance.calendarContainer;

          if (calendar) {
            calendar.classList.remove('open');
            calendar.style.transition = '';
          }

          document.body.classList.remove('flatpickr-open');
        },

        onChange: (selectedDates) => {
          if (selectedDates.length > 0) {
            if (enableTime) {
              const date = selectedDates[0];
              const formatted = date.toISOString().slice(0, 16);
              onChange?.(formatted);
            } else {
              onChange?.(selectedDates[0].toISOString().split('T')[0]);
            }
          }
        },
      });
    }
  }, [value, onChange, enableTime, disabled]);

  useEffect(() => {
    if (flatpickrInstance.current) {
      flatpickrInstance.current.set('clickOpens', !disabled);
    }
  }, [disabled]);

  const baseClasses =
    'form-datepicker w-full bg-transparent px-4 py-3.5 text-sm text-black outline-none placeholder:text-body dark:text-white dark:placeholder:text-bodydark2';

  return (
    <div className={containerClass} data-field={name}>
      <label
        className="
          mb-2.5
          flex
          items-center
          gap-1
          text-sm
          font-medium
          text-black
          dark:text-white
        "
      >
        {label}
        {required && <RequiredAsteriskTooltip delay={400} />}
      </label>

      <div
        className={`
        bg-gray-1
        flex
        w-full
        overflow-hidden
        rounded-xl
        border
        border-stroke
        transition-all
        duration-200
        focus-within:border-primary
        focus-within:ring-2
        focus-within:ring-primary/10
        dark:border-strokedark
        dark:bg-meta-4
        ${disabled ? 'cursor-not-allowed opacity-70' : ''}
      `}
      >
        {iconKey && (
          <div
            className="
            flex
            shrink-0
            items-center
            justify-center
            border-r
            border-stroke
            bg-gray-2
            px-4
            dark:border-strokedark
            dark:bg-boxdark-2
          "
          >
            <InputIconByKey iconKey={iconKey} />
          </div>
        )}

        <input
          ref={datePickerRef}
          type="text"
          name={name}
          value={value}
          placeholder={placeholder}
          onChange={(e) => onChange?.(e.target.value)}
          disabled={disabled}
          data-class="flatpickr-right"
          autoComplete="off"
          className={baseClasses}
        />
      </div>

      {helperText && !error && (
        <p className="mt-1 text-xs text-body dark:text-bodydark2">
          {helperText}
        </p>
      )}

      {error && <p className="mt-2 text-sm text-danger">{error}</p>}
    </div>
  );
};

export default DatePicker;
